import { SegmentedControl } from '@mantine/core';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTravelRequestFilters } from '@/module/Travel/Registry/useTravelRequestFilters';
import { TravelRequestCard } from '@/module/Travel/Request/components/Card/TravelRequestCard';
import { TravelOverlapCardFrame } from '@/module/Travel/Request/components/TravelOverlapButton';

import { Browse, NoContentPlaceholder, usePagination } from '@/core/components';

import { RoleType, useCurrentUser } from '@feature/security';
import { FilterBox, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { getTravelApprovalPagedOptions } from './model/TravelApprovalQuery';
import { TravelApprovalFilter } from './model/TravelApprovalTypes';

const travelBoardSegmentList = ['toSend', 'awaiting'] as const;
type TravelBoardSegment = (typeof travelBoardSegmentList)[number];

export function TravelBoardRequestBrowse() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (currentRole !== RoleType.RESEARCH_ADMINISTRATIVE_OFFICE) {
    return <NoContentPlaceholder title={t('travel.approvals.board.noAccess')} />;
  }

  return <TravelBoardRequestList />;
}

function TravelBoardRequestList() {
  const { t } = useTranslation();
  const [segment, setSegment] = useState<TravelBoardSegment>('toSend');
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, resetPagination, ...paginationProps } = usePagination({ search });
  // Every row here is pending, hence SUBMITTED, so the status filter would never narrow anything.
  const { filter: requestFilter, fields } = useTravelRequestFilters({
    initialYear: null,
    showStatuses: false,
    restrictDeanFaculty: false,
    onChange: resetPagination,
  });

  const segmentParams = (value: TravelBoardSegment) => {
    const filter: TravelApprovalFilter = {
      ...requestFilter,
      pendingOnly: null,
      stepKeys: ['ADMINISTRATIVE_BOARD'],
      sentToBoard: value === 'awaiting',
    };
    return { ...filter, sortDirection, search };
  };

  const { data } = useSuspenseQuery(getTravelApprovalPagedOptions({ ...pagination, ...segmentParams(segment) }));
  useTotalElements(data);

  // A one-row page, read only for its total, so the closed segment's label follows the filters too.
  const otherSegment: TravelBoardSegment = segment === 'toSend' ? 'awaiting' : 'toSend';
  const otherCount = useQuery(getTravelApprovalPagedOptions({ page: 0, pageSize: 1, ...segmentParams(otherSegment) }));
  const otherTotal = otherCount.isError ? '?' : (otherCount.data?.count ?? '-');
  const segmentTotal = (value: TravelBoardSegment) => (value === segment ? data.count : otherTotal);

  return (
    <Browse
      actions={null}
      title={t('travel.approvals.board.title')}
      pagination={paginationProps}
      header={
        <SegmentedControl
          aria-label={t('travel.approvals.board.segmentLabel')}
          value={segment}
          onChange={value => {
            setSegment(value as TravelBoardSegment);
            resetPagination();
          }}
          data={travelBoardSegmentList.map(value => ({
            value,
            label: t(`travel.approvals.board.segmentEnum.${value}`, { total: segmentTotal(value) }),
          }))}
        />
      }
      filters={
        <FilterBox>
          <SearchFilter
            label={t('label.search')}
            description={t('travel.approvals.searchBy')}
            {...searchProps}
          />
          {fields}
        </FilterBox>
      }
      sortDirection={sortDirection}
      sortedBy={t('travel.approvals.sortedBy')}
      toggleSortDirection={toggleSortDirection}
      data={data.list}
      renderItem={request => (
        <TravelOverlapCardFrame
          key={request.id}
          requestId={request.id}
          overlapCount={request.overlapCount}
        >
          <TravelRequestCard
            request={request}
            to={`/travel/requests/${request.id}`}
          />
        </TravelOverlapCardFrame>
      )}
    />
  );
}
